import {
  IndexDataTable,
  IndexTableClearFiltersButton,
  IndexTableSearchBox,
  IndexTableToolbar,
  type IndexDataTableRowPresentation,
} from '@/components/tables/IndexTable'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ToggleButton } from '@/components/ui/toggle-button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Typography } from '@/components/ui/typography'
import { ViewModeSelector, type ViewModeOption } from '@/components/patterns/view-mode-selector'
import type {
  AvailabilityCalendarOccurrence,
  AvailabilityChange,
  AvailabilityTemporaryHoursWindow,
} from '@/lib/services/availability-changes/availability-changes.types'
import { cn } from '@/lib/utils'
import type { ColumnDef } from '@tanstack/react-table'
import {
  Ban,
  Calendar,
  CalendarClock,
  CalendarDays,
  Check,
  Clock,
  Flower2,
  History,
  List,
  MapPin,
  Repeat2,
  ShoppingBasket,
  SlidersHorizontal,
  Truck,
  X,
} from 'lucide-react'
import {
  INDEFINITE_AVAILABILITY_CHANGE_END_LABEL,
  availabilityChangeDefaultSorting,
  changeEndsIndefinitely,
  formatAvailabilityDate,
  getAvailabilityChangeCountText,
  getAvailabilityChangeDisplayTitle,
  getChangeEndDate,
  getChangeIdentifier,
  getChangeLocationSummary,
  getChangeStartDate,
  getTemporaryHoursSummary,
  formatTemporaryHour,
  changeIsSingleDay,
  changeIsPastOneTime,
  sortAvailabilityChangesByUpcomingStart,
  type AvailabilityChangeFields,
  type AvailabilityChangeFilter,
  type AvailabilityLocationEntry,
} from '@/lib/services/availability-changes/availability-changes.utils'
import { useMemo, type ReactElement, type ReactNode } from 'react'

/** A saved change, or a calendar occurrence when the list shows one day of the calendar. */
type AvailabilityChangeListRow = AvailabilityChange | AvailabilityCalendarOccurrence

const availabilityChangeFilterOptions: {
  value: AvailabilityChangeFilter
  label: string
  icon: ReactNode
}[] = [
  { value: 'all', label: 'All', icon: <SlidersHorizontal className="size-4" /> },
  { value: 'closed', label: 'Closed', icon: <Ban className="size-4" /> },
  {
    value: 'pickup_unavailable',
    label: 'Pickup pause',
    icon: <ShoppingBasket className="size-4" />,
  },
  { value: 'delivery_unavailable', label: 'Delivery pause', icon: <Truck className="size-4" /> },
  {
    value: 'designers_choice_only',
    label: "Designer's Choice",
    icon: <Flower2 className="size-4" />,
  },
  { value: 'temporary_hours', label: 'Temporary hours', icon: <Clock className="size-4" /> },
]

const availabilityChangeLayouts: ViewModeOption[] = [
  { value: 'calendar', label: 'Calendar', icon: Calendar },
  { value: 'list', label: 'List', icon: List },
]

export function getAvailabilityChangeRowPresentation(
  change: AvailabilityChangeFields
): IndexDataTableRowPresentation {
  return changeIsPastOneTime(change) ? { tone: 'muted', contentTone: 'muted' } : { tone: 'neutral' }
}

function ChangeRecurrenceMeta({
  change,
  className,
}: {
  change: AvailabilityChangeFields
  className?: string
}) {
  return (
    <Typography variant="caption" className={cn('flex items-center gap-1', className)}>
      {change.repeats_annually ? (
        <>
          <Repeat2 className="size-3" />
          Every year
        </>
      ) : (
        <>
          <CalendarClock className="size-3" />
          One time
        </>
      )}
    </Typography>
  )
}

function AvailabilityChangeFilterSelect({
  value,
  onValueChange,
}: {
  value: AvailabilityChangeFilter
  onValueChange: (value: AvailabilityChangeFilter) => void
}) {
  const isFiltered = value !== 'all'

  return (
    <div className="flex items-center">
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger selected={isFiltered} className="w-fit">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {availabilityChangeFilterOptions.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.icon}
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

function AvailabilityChangeLocationSelect({
  options,
  value,
  onValueChange,
}: {
  options: AvailabilityLocationEntry[]
  value: string
  onValueChange: (value: string) => void
}) {
  if (options.length <= 1) {
    return null
  }

  const isFiltered = value !== 'all'

  return (
    <div className="flex items-center">
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger
          selected={isFiltered}
          className="w-fit max-w-52"
          aria-label="Filter by location"
        >
          <SelectValue placeholder="All locations" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">
            <MapPin className="size-4" />
            All locations
          </SelectItem>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              <MapPin className="size-4" />
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

function AvailabilityChangePastEventsToggle({
  pressed,
  onPressedChange,
}: {
  pressed: boolean
  onPressedChange: (pressed: boolean) => void
}) {
  return (
    <ToggleButton
      type="button"
      pressed={pressed}
      size="sm"
      className="h-9 gap-2 px-3 has-[>svg]:px-3 has-[>svg]:pt-0"
      onClick={() => onPressedChange(!pressed)}
    >
      <History className="size-4" />
      {pressed ? 'Hide past events' : 'Show past events'}
    </ToggleButton>
  )
}

export function AvailabilityChangesHeader({
  calendarControls,
  filter,
  hasClearableFilters,
  isFetching,
  layout,
  locationFilter,
  locationOptions,
  resultCount,
  searchTerm,
  showPastChanges,
  onClearFilters,
  onFilterChange,
  onLocationFilterChange,
  onLayoutChange,
  onShowPastChangesChange,
  onSearchCommit,
}: {
  calendarControls: ReactNode
  filter: AvailabilityChangeFilter
  hasClearableFilters: boolean
  isFetching: boolean
  layout: string
  /** `all`, or a location option's `id:{id}` value. */
  locationFilter: string
  /** Empty hides the location filter. */
  locationOptions: AvailabilityLocationEntry[]
  resultCount: number
  searchTerm: string
  showPastChanges: boolean
  onClearFilters: () => void
  onFilterChange: (filter: AvailabilityChangeFilter) => void
  onLocationFilterChange: (locationFilter: string) => void
  onLayoutChange: (layout: string) => void
  onShowPastChangesChange: (showPastChanges: boolean) => void
  onSearchCommit: (searchTerm: string) => void
}): ReactElement {
  return (
    <IndexTableToolbar
      primary={
        <IndexTableSearchBox
          value={searchTerm}
          onCommit={onSearchCommit}
          countText={getAvailabilityChangeCountText(resultCount)}
          isFetching={isFetching}
          placeholder="Search changes..."
        />
      }
      secondary={
        <>
          {hasClearableFilters ? <IndexTableClearFiltersButton onClick={onClearFilters} /> : null}
          {calendarControls}
          <AvailabilityChangeLocationSelect
            options={locationOptions}
            value={locationFilter}
            onValueChange={onLocationFilterChange}
          />
          {layout === 'list' ? (
            <AvailabilityChangePastEventsToggle
              pressed={showPastChanges}
              onPressedChange={onShowPastChangesChange}
            />
          ) : null}
          <AvailabilityChangeFilterSelect value={filter} onValueChange={onFilterChange} />
          <ViewModeSelector
            value={layout}
            onValueChange={onLayoutChange}
            options={availabilityChangeLayouts}
            label="Availability view"
          />
        </>
      }
    />
  )
}

function AvailabilityStateIcon({ isAvailable, label }: { isAvailable: boolean; label: string }) {
  return isAvailable ? (
    <span
      className="inline-flex size-8 items-center justify-center rounded-md text-success"
      aria-label={label}
      title={label}
    >
      <Check className="size-4" />
    </span>
  ) : (
    <span
      className="inline-flex size-8 items-center justify-center rounded-md text-destructive"
      aria-label={label}
      title={label}
    >
      <X className="size-4" />
    </span>
  )
}

function NeutralStateIcon({ label }: { label: string }) {
  return <NotApplicableText label={label} />
}

function NotApplicableText({ label, className }: { label: string; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex h-8 min-w-8 items-center justify-center rounded-md text-xs font-medium text-muted-foreground',
        className
      )}
      aria-label={label}
      title={label}
    >
      n/a
    </span>
  )
}

function getTemporaryServiceLabel(
  serviceLabel: string,
  hours: AvailabilityTemporaryHoursWindow
): string {
  const open = formatTemporaryHour(hours?.open)
  const close = formatTemporaryHour(hours?.close)

  return open && close
    ? `${serviceLabel} hours changed to ${open} - ${close}`
    : `${serviceLabel} hours changed`
}

function getTemporaryServiceTimeText(hours: AvailabilityTemporaryHoursWindow): string {
  const open = formatTemporaryHour(hours?.open)
  const close = formatTemporaryHour(hours?.close)

  return open && close ? `${open} - ${close}` : 'Custom hours'
}

function TemporaryHoursServiceCell({
  hours,
  serviceLabel,
}: {
  hours: AvailabilityTemporaryHoursWindow | undefined
  serviceLabel: string
}) {
  if (!hours?.is_open) {
    return <NeutralStateIcon label={`${serviceLabel} follows regular hours`} />
  }

  const label = getTemporaryServiceLabel(serviceLabel, hours)

  return (
    <Badge variant="info" size="xs" wrap aria-label={label} title={label}>
      <Clock className="size-3 shrink-0" />
      <span className="min-w-0 truncate">{getTemporaryServiceTimeText(hours)}</span>
    </Badge>
  )
}

function StoreAvailabilityCell({ change }: { change: AvailabilityChangeFields }) {
  if (getTemporaryHoursSummary(change)) {
    return <NeutralStateIcon label="Store availability follows regular settings" />
  }

  return (
    <AvailabilityStateIcon
      isAvailable={!change.store_closed}
      label={change.store_closed ? 'Store closed' : 'Store open'}
    />
  )
}

function ServiceAvailabilityCell({
  change,
  service,
}: {
  change: AvailabilityChangeFields
  service: {
    key: 'pickup' | 'delivery'
    label: string
    unavailableKey: 'pickup_paused' | 'delivery_paused'
  }
}) {
  const unavailable = Boolean(change[service.unavailableKey])

  if (unavailable) {
    return <AvailabilityStateIcon isAvailable={false} label={`${service.label} unavailable`} />
  }

  const summary = getTemporaryHoursSummary(change)

  if (summary) {
    return (
      <TemporaryHoursServiceCell
        hours={change.temporary_hours?.[service.key]}
        serviceLabel={service.label}
      />
    )
  }

  return <AvailabilityStateIcon isAvailable label={`${service.label} available`} />
}

function ChangeFeatureIcon({ active, label }: { active: boolean; label: string }) {
  return active ? (
    <span
      className="inline-flex size-8 items-center justify-center rounded-md text-success"
      aria-label={label}
      title={label}
    >
      <Check className="size-4" />
    </span>
  ) : (
    <NotApplicableText label={label} />
  )
}

function getTemporaryHoursChangeTitle(change: AvailabilityChangeFields): string {
  const hasPickupHours = Boolean(change.temporary_hours?.pickup?.is_open)
  const hasDeliveryHours = Boolean(change.temporary_hours?.delivery?.is_open)

  if (hasPickupHours && hasDeliveryHours) {
    return 'Temporary pickup and delivery hours'
  }

  if (hasPickupHours) {
    return 'Temporary pickup hours'
  }

  if (hasDeliveryHours) {
    return 'Temporary delivery hours'
  }

  return 'Temporary hours'
}

function getAvailabilityChangeListTitle(change: AvailabilityChangeFields): string {
  const temporaryHoursSummary = getTemporaryHoursSummary(change)
  const displayTitle = getAvailabilityChangeDisplayTitle(change)

  return temporaryHoursSummary && displayTitle === temporaryHoursSummary
    ? getTemporaryHoursChangeTitle(change)
    : displayTitle
}

function getAvailabilityChangeColumns({
  showLocationSummary = true,
}: { showLocationSummary?: boolean } = {}): ColumnDef<AvailabilityChangeListRow>[] {
  return [
    {
      id: 'title',
      accessorFn: getAvailabilityChangeDisplayTitle,
      header: 'Change',
      enableSorting: true,
      cell: ({ row }) => {
        const change = row.original

        return (
          <>
            <span className="block truncate font-semibold">
              {getAvailabilityChangeListTitle(change)}
            </span>
            {showLocationSummary ? (
              <Typography variant="caption" className="mt-1 flex min-w-0 items-center gap-1">
                <MapPin className="size-3 shrink-0" />
                <span className="truncate">{getChangeLocationSummary(change)}</span>
              </Typography>
            ) : null}
          </>
        )
      },
      meta: {
        label: 'Change',
        kind: 'primary',
        icon: CalendarDays,
        minWidth: '13rem',
        preferredWidth: '15rem',
      },
    },
    {
      id: 'start_date',
      accessorFn: getChangeStartDate,
      header: 'Start',
      enableSorting: true,
      sortingFn: sortAvailabilityChangesByUpcomingStart,
      cell: ({ row }) => {
        const change = row.original

        return (
          <>
            <span className="font-medium">
              {formatAvailabilityDate(getChangeStartDate(change))}
            </span>
            <ChangeRecurrenceMeta change={change} className="mt-1" />
          </>
        )
      },
      meta: {
        label: 'Start',
        kind: 'date',
        icon: CalendarDays,
      },
    },
    {
      id: 'end_date',
      accessorFn: getChangeEndDate,
      header: 'End',
      enableSorting: true,
      cell: ({ row }) => {
        const change = row.original

        return (
          <span className={cn('font-medium', changeIsSingleDay(change) && 'text-muted-foreground')}>
            {changeEndsIndefinitely(change) ? (
              INDEFINITE_AVAILABILITY_CHANGE_END_LABEL
            ) : changeIsSingleDay(change) ? (
              <NotApplicableText label="No separate end date" className="h-auto min-w-0" />
            ) : (
              formatAvailabilityDate(getChangeEndDate(change))
            )}
          </span>
        )
      },
      meta: {
        label: 'End',
        kind: 'date',
        icon: CalendarClock,
      },
    },
    {
      id: 'store_closed',
      accessorFn: (change) => Boolean(change.store_closed),
      header: 'Store',
      enableSorting: true,
      cell: ({ row }) => <StoreAvailabilityCell change={row.original} />,
      meta: {
        label: 'Store',
        kind: 'badge',
        icon: Ban,
        align: 'center',
      },
    },
    {
      id: 'pickup',
      accessorFn: (change) => Boolean(change.pickup_paused),
      header: 'Pickup',
      enableSorting: true,
      cell: ({ row }) => (
        <ServiceAvailabilityCell
          change={row.original}
          service={{ key: 'pickup', label: 'Pickup', unavailableKey: 'pickup_paused' }}
        />
      ),
      meta: {
        label: 'Pickup',
        kind: 'badge',
        icon: ShoppingBasket,
        align: 'center',
      },
    },
    {
      id: 'delivery',
      accessorFn: (change) => Boolean(change.delivery_paused),
      header: 'Delivery',
      enableSorting: true,
      cell: ({ row }) => (
        <ServiceAvailabilityCell
          change={row.original}
          service={{ key: 'delivery', label: 'Delivery', unavailableKey: 'delivery_paused' }}
        />
      ),
      meta: {
        label: 'Delivery',
        kind: 'badge',
        icon: Truck,
        align: 'center',
      },
    },
    {
      id: 'designers_choice_only',
      accessorFn: (change) => Boolean(change.designers_choice_only),
      header: 'Choice',
      enableSorting: true,
      cell: ({ row }) => {
        const active = Boolean(row.original.designers_choice_only)

        return (
          <ChangeFeatureIcon
            active={active}
            label={active ? "Designer's Choice required" : "Designer's Choice not required"}
          />
        )
      },
      meta: {
        label: 'Choice',
        mobileLabel: "Designer's Choice",
        kind: 'badge',
        icon: Flower2,
        align: 'center',
        minWidth: '5.25rem',
        preferredWidth: '5.75rem',
      },
    },
  ]
}

export function AvailabilityChangeList({
  onChangeClick,
  changes = [],
  showLocationSummary = true,
  toolbar,
}: {
  onChangeClick?: ((change: AvailabilityChangeListRow) => void) | undefined
  changes?: AvailabilityChangeListRow[]
  showLocationSummary?: boolean
  toolbar?: ReactNode
}): ReactElement {
  const columns = useMemo(
    () => getAvailabilityChangeColumns({ showLocationSummary }),
    [showLocationSummary]
  )

  return (
    <IndexDataTable
      data={changes}
      columns={columns}
      mode="client"
      initialSorting={availabilityChangeDefaultSorting}
      pagination={changes.length > 20}
      maxPageSize={50}
      defaultPageSize={20}
      getRowId={(change) => String(getChangeIdentifier(change))}
      onRowClick={onChangeClick}
      getRowPresentation={getAvailabilityChangeRowPresentation}
      toolbar={toolbar}
      emptyState={
        <div className="py-5 text-center">
          <Typography variant="body" weight="medium">
            No availability changes found
          </Typography>
          <Typography variant="supporting" className="mt-1 block">
            Schedule a closure, service limit, or hour change.
          </Typography>
        </div>
      }
      tableClassName="text-sm"
    />
  )
}
